import { PROJECT_VISIBILITIES, type ProjectVisibility } from '@/api';
import { AppRoutes } from '@/constants';

/** The filterable columns; each id is also the URL param that holds its values. */
export const PROJECT_LIST_FILTER_COLUMNS = [
  { key: 'owner', label: 'Owner' },
  { key: 'team', label: 'Team' },
  { key: 'visibility', label: 'Visibility' },
] as const;

export const PROJECT_LIST_SEARCH_PARAM = 'search';

/** Every URL param the project list owns. */
const PROJECT_LIST_URL_PARAMS: readonly string[] = [
  ...PROJECT_LIST_FILTER_COLUMNS.map(({ key }) => key),
  PROJECT_LIST_SEARCH_PARAM,
];

/** Drops the project list's filters and search, so they stay behind when the user navigates away from it. */
export function withoutProjectListParams(searchParams: URLSearchParams, pathname: string): URLSearchParams {
  if (pathname !== `/${AppRoutes.Projects}`) return searchParams;

  const carried = new URLSearchParams(searchParams);
  PROJECT_LIST_URL_PARAMS.forEach((param) => carried.delete(param));
  return carried;
}

export type ProjectListFilterKey = (typeof PROJECT_LIST_FILTER_COLUMNS)[number]['key'];

export type ProjectListFilters = Record<ProjectListFilterKey, string[]>;

export const isProjectListFilterKey = (columnId: string): columnId is ProjectListFilterKey =>
  PROJECT_LIST_FILTER_COLUMNS.some(({ key }) => key === columnId);

const isProjectVisibility = (value: string): value is ProjectVisibility =>
  (PROJECT_VISIBILITIES as readonly string[]).includes(value);

export const readProjectListFilters = (searchParams: URLSearchParams): ProjectListFilters => ({
  owner: searchParams.getAll('owner'),
  team: searchParams.getAll('team'),
  visibility: searchParams.getAll('visibility').filter(isProjectVisibility),
});
